import { type ApolloError, type QueryResult, useLazyQuery } from '@apollo/client';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { DownloadManager, queueAndDownload } from 'src/download';
import {
  buildDownloadAvailabilityQ,
  buildDownloadLinksQ,
  buildSharedCollectionDownloadLinksQ,
  buildSharedDownloadAvailabilityQ,
  buildSharedDownloadLinksQ,
} from 'src/dynamoQueries/download';
import { getCollectionItemGlobalIds } from 'src/helpers';
import { useCollectionSimpleLazyQuery } from 'src/hooks/collection';
import { onDownloadLinkErrorMsg } from 'src/messages/error';
import { useCustomToast } from './menu';

// Helper to extract error message from unknown error
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'An unknown error occurred';
};

// Collection item node structure for type safety
interface CollectionItemNode {
  node: {
    contentObject: {
      id: string;
    };
  };
}

// Download link structure returned from GraphQL
interface DownloadLink {
  downloadPath: string;
  filename: string;
  globalId?: string;
  formatType?: string;
}

// Options for download operations
interface DownloadOptions {
  formatType?: string;
  fallbackPolicy?: 'SKIP_MISSING' | 'FALLBACK_CHAIN' | 'STRICT';
}

// Availability entry structure
interface AvailabilityEntry {
  formatType: string;
  total: number;
  available: number;
}

// GraphQL error extension
interface GraphQLErrorExtension {
  code?: string;
}

// GraphQL error with extensions
interface GraphQLError {
  message: string;
  extensions?: GraphQLErrorExtension;
}

// Download links query response
interface DownloadLinksData {
  downloadLinks?: DownloadLink[];
  sharedDownloadLinks?: DownloadLink[];
}

// Availability query response
interface AvailabilityData {
  downloadAvailability?: AvailabilityEntry[];
  sharedDownloadAvailability?: AvailabilityEntry[];
}

// Shared collection download links response
interface SharedCollectionDownloadLinksData {
  sharedCollectionDownloadLinks?: DownloadLink[];
}

// Return type for useAssetDownload
interface UseAssetDownloadReturn {
  handleAssetDownload: (globalIds: string[], options?: DownloadOptions) => Promise<void>;
}

// Return type for useCollectionDownload
interface UseCollectionDownloadReturn {
  downloadGallery: () => void;
  initiateDownload: (options?: DownloadOptions) => void;
  galleryReady: boolean;
  downloadCount: number;
  prAvailable: number | null;
  prTotal: number | null;
  globalIds: string[];
  loading: boolean;
  error: ApolloError | undefined;
  setGalleryReady: Dispatch<SetStateAction<boolean>>;
}

// Return type for useSharedCollectionBulkDownload
interface UseSharedCollectionBulkDownloadReturn {
  startSharedCollectionDownload: (options: DownloadOptions) => Promise<void>;
}

export const useAssetDownload = (shared: boolean): UseAssetDownloadReturn => {
  const { toastr: cToastr } = useCustomToast();
  const [getDownloadLinks] = useLazyQuery<DownloadLinksData>(
    shared ? buildSharedDownloadLinksQ() : buildDownloadLinksQ(),
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  );

  const handleAssetDownload = async (globalIds: string[], options: DownloadOptions = {}): Promise<void> => {
    // Show loading toast
    const loadingToast = cToastr.info('Preparing download...');
    try {
      const { data, loading, errors } = await getDownloadLinks({
        variables: {
          globalIds,
          formatType: options.formatType,
          fallback: options.fallbackPolicy,
        },
      });

      if (errors) {
        errors.forEach((e: GraphQLError) => {
          if (e?.extensions?.code === 'BULK_DOWNLOAD_LIMIT_EXCEEDED') {
            cToastr.clear();
            cToastr.error(e.message || 'Bulk download limit exceeded. Please reduce your selection.');
            return;
          }
          cToastr.clear();
          cToastr.error(e.message);
          return;
        });
        return;
      }

      if (!loading) {
        cToastr.clear(loadingToast);
      }

      if (!data) {
        cToastr.error('No response from download service');
        return;
      }

      const { downloadLinks: regularDownloadLinks, sharedDownloadLinks } = data;
      const downloadLinks = shared ? sharedDownloadLinks : regularDownloadLinks;
      const manager = new DownloadManager(6, true);

      if (downloadLinks) {
        cToastr.success('Download started');
        queueAndDownload({ downloadLinks, manager, toastr: cToastr });
        return;
      }
      if (!downloadLinks) {
        cToastr.error('No download links available');
        return;
      }
    } catch (error) {
      // Dismiss loading toast and show error toast
      cToastr.error(onDownloadLinkErrorMsg(getErrorMessage(error)));
    }
  };
  return { handleAssetDownload };
};

export const useCollectionDownload = (collectionId: string, isShared = false): UseCollectionDownloadReturn => {
  const { toastr } = useCustomToast();
  const { handleAssetDownload } = useAssetDownload(isShared);
  const [galleryReady, setGalleryReady] = useState<boolean>(false);
  const [globalIds, setGlobalIds] = useState<string[]>([]);
  const [prAvailable, setPrAvailable] = useState<number | null>(null);
  const [prTotal, setPrTotal] = useState<number | null>(null);

  // Availability query for gallery quick action
  const [getAvailability] = useLazyQuery<AvailabilityData>(
    isShared ? buildSharedDownloadAvailabilityQ() : buildDownloadAvailabilityQ(),
    {
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  );

  const [getGallery, { loading, error }] = useCollectionSimpleLazyQuery({
    variables: {
      id: collectionId,
      first: 1000,
    },
    options: {
      onCompleted: async (data: Record<string, { items?: { edges?: CollectionItemNode[] } }>) => {
        const itemEdges = isShared ? data?.sharedCollection?.items?.edges : data?.collection?.items?.edges;
        const ids = getCollectionItemGlobalIds((itemEdges || []) as CollectionItemNode[]);
        setGlobalIds(ids);
        if (ids.length === 0) {
          toastr.warning('No downloadable items found in this gallery');
          setGalleryReady(false);
          return;
        }
        // Query availability for Proxy (PR) and inform the user before showing confirm
        try {
          const { data: availData } = await getAvailability({ variables: { globalIds: ids } });
          const entries = (isShared ? availData?.sharedDownloadAvailability : availData?.downloadAvailability) || [];
          const pr = entries.find((e) => e.formatType === 'PR');
          const total = pr ? pr.total : ids.length;
          const available = pr ? pr.available : 0;
          setPrTotal(total);
          setPrAvailable(available);
          const skipped = total - available;
          toastr.info(
            `Proxy available for ${available} of ${total} items${skipped > 0 ? ` (will skip ${skipped})` : ''}.`,
          );
        } catch (_e) {
          // If availability fails, continue with best-effort download
          setPrAvailable(null);
          setPrTotal(null);
          toastr.info('Proxy availability check failed; you can still start the download.');
        }
        // Signal UI to open confirmation now that availability (if any) is ready
        setGalleryReady(true);
        // Do not auto-start here; caller will confirm and initiate download
      },
      fetchPolicy: 'network-only',
    },
    lazy: true,
    shared: isShared,
  }) as [() => void, QueryResult<Record<string, { items?: { edges?: unknown[] } }>>];

  const downloadGallery = (): void => {
    getGallery();
  };

  const initiateDownload = (options: DownloadOptions = {}): void => {
    handleAssetDownload(globalIds, options);
  };

  if (loading) {
    toastr.info('Loading Gallery for download...');
  }
  if (error) {
    toastr.error('Error loading Gallery for download');
  }

  return {
    downloadGallery,
    initiateDownload,
    galleryReady,
    downloadCount: globalIds.length,
    prAvailable,
    prTotal,
    globalIds,
    loading,
    error,
    setGalleryReady,
  };
};

/**
 * useSharedCollectionBulkDownload
 * Dedicated hook for shared gallery "Download All" using collection-scoped endpoint with 100 cap.
 */
export const useSharedCollectionBulkDownload = (collectionId: string): UseSharedCollectionBulkDownloadReturn => {
  const { toastr: cToastr } = useCustomToast();
  const [getLinks] = useLazyQuery<SharedCollectionDownloadLinksData>(buildSharedCollectionDownloadLinksQ(), {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  const startSharedCollectionDownload = async (options: DownloadOptions): Promise<void> => {
    const loadingToast = cToastr.info('Preparing download...');
    try {
      const { data, errors, loading } = await getLinks({
        variables: {
          collectionId,
          formatType: options?.formatType,
          fallback: options?.fallbackPolicy || 'SKIP_MISSING',
        },
      });
      if (!loading) cToastr.clear(loadingToast);
      if (errors?.length) {
        errors.forEach((e: GraphQLError) => {
          if (e?.extensions?.code === 'BULK_DOWNLOAD_LIMIT_EXCEEDED') {
            cToastr.error(e.message || 'Bulk download limit exceeded. Please reduce your selection.');
          } else {
            cToastr.error(e.message || 'Failed to generate download links');
          }
        });
        return;
      }
      const downloadLinks = data?.sharedCollectionDownloadLinks || [];
      if (!downloadLinks.length) {
        cToastr.warning('No items available for the selected format.');
        return;
      }
      const manager = new DownloadManager(6, true);
      cToastr.success('Download started');
      queueAndDownload({ downloadLinks, manager, toastr: cToastr });
    } catch (error) {
      cToastr.clear(loadingToast);
      cToastr.error(onDownloadLinkErrorMsg(getErrorMessage(error)));
    }
  };

  return { startSharedCollectionDownload };
};
